<template>
  <UIPopup
    v-model="modelValue"
    :closeable="false"
    hide-buttons
    :max-width="isDesktop ? '768px' : isTablet ? '553px' : ''"
  >
    <template #header>
      <div class="mx-auto flex max-w-[488px] flex-col items-center justify-center gap-5">
        <UIButton
          id="back"
          variant="ghost"
          left-icon="arrow-big-filled-left"
          class="!absolute left-4 top-4 fill-gold-primary-20 !text-black-100 tablet:left-10 tablet:top-10"
          @click="goBack"
        >
          {{ $t('payment.start.navigation.payment') }}
        </UIButton>
        <div class="mt-12 flex w-full items-center gap-2 rounded-full bg-bg p-1 tablet:mt-16 tablet:p-2 desktop:mb-9">
          <div class="h-4 w-1/5 rounded-full bg-gold-primary-100"></div>
          <div class="h-4 w-1/5 rounded-full bg-gold-primary-100"></div>
          <div class="h-4 w-1/5 rounded-full bg-gold-primary-100"></div>
          <div class="h-4 w-1/5 rounded-full bg-gold-primary-100"></div>
          <div class="h-4 w-1/5 rounded-full bg-gold-primary-50"></div>
        </div>
        <h1 class="text-center text-2xl font-medium text-black-100">{{ $t('payment.start.header.title') }}</h1>
        <div v-if="hasPackageData" class="text-center">
          <p class="text-sm text-black-60 mb-1">Selected Plan</p>
          <p class="text-lg font-bold text-black-100">{{ packageName }}</p>
          <p class="text-base text-black-80">${{ packagePrice }}/{{ packagePeriod }}</p>
        </div>
        <p v-else class="text-base font-bold text-black-80">{{ $t('payment.start.header.paymentType') }}</p>
      </div>
    </template>
    <template #body>
      <div class="mx-auto max-w-[488px]">
        <span class="text-base">{{ $t('payment.start.paymentMethods.title') }}</span>
        <hr class="my-5 w-full border-t border-solid border-black-20" />
        <div class="flex w-full flex-col gap-3">
          <UIButton
            id="pay"
            class="w-full"
            :loading="isPending"
            variant="text-black-100"
            loader-color="white"
            left-icon="credit-card"
            @click="handleCreditCardClick"
          >
            {{ $t('payment.start.paymentMethods.creditCard', { amount: `$${packagePrice}` }) }}
          </UIButton>
          <PaymentPaypalButtons
            :amount="packagePrice"
            :package-id="packageId"
            :paypal-plan-id="paypalPlanId"
            @close="modelValue = false"
          />
        </div>
        <div class="mt-10 flex w-full flex-col gap-1 text-black-80">
          <div class="flex flex-row items-center justify-center gap-1 text-xs tablet:justify-start">
            {{ $t('payment.start.footer.moneyBack.guarantee') }}
            <b>{{ $t('payment.start.footer.moneyBack.noQuestions') }}</b>
          </div>
          <hr class="w-full border-t border-solid border-black-20" />
          <div class="flex flex-col items-center justify-between gap-4 text-xs tablet:flex-row">
            <span>{{ $t('payment.start.footer.security') }}</span>
            <UIIcon name="banks" size="unset" class="!h-4 !w-fit" />
          </div>
        </div>
      </div>
    </template>
  </UIPopup>
</template>

<script setup lang="ts">
const modelValue = defineModel<boolean>()

const props = defineProps<{
  packageId?: string
  packageName?: string
  packagePrice?: number
  packagePeriod?: string
  paypalPlanId?: string
}>()

const hasPackageData = computed(() => 
  props.packageId != null && 
  props.packageName != null && 
  props.packagePrice != null && 
  props.packagePeriod != null
)

const uiStore = useUIStore()
const authStore = useAuthStore()

const { isTablet, isDesktop } = useAppBreakpoints()

const { mutateAsync: goToPaymentLink, isPending: isPaymentPending } = useCreatePaymentLink()
const { mutateAsync: createPackagePaymentLink, isPending: isPackagePending } = useCreatePackagePaymentLink()
const { mutateAsync: schedulePaymentCheck } = useSchedulePaymentCheck()

const gtm = useGtm()
const isPending = computed(() => isPaymentPending.value || isPackagePending.value)

const handleCreditCardClick = async () => {
  if (props.packageId) {
    await createPackagePaymentLink(props.packageId)
  } else {
    await goToPaymentLink()
  }
}

const goBack = () => {
  modelValue.value = false
}

onMounted(async () => {
  gtm?.trackEvent({ event: 'checkout-started' })
  if (authStore.getUser?.id) {
    try {
      await schedulePaymentCheck({
        userId: String(authStore.getUser.id),
        leadId: authStore.getUser?.leadId || '',
        name: `${authStore.getUser?.firstName} ${authStore.getUser?.lastName}`,
        packageId: props.packageId || authStore.getUser?.packageId || undefined,
      })
    } catch (error) {
      console.error('Failed to schedule payment status check:', error)
    }
  }
})

watch(
  () => modelValue.value,
  (isOpen, wasOpen) => {
    if (wasOpen && !isOpen && authStore.getUser?.id) {
      sendNotification('abandoned').catch(() => {})
    }
  }
)
</script>
